/**
 * Calculates appropriate y-axis range for chart visualization.
 *
 * This function improves chart readability when dealing with data where the minimum value
 * is large and close to the maximum value. It adjusts the y-axis range to provide better
 * visual separation between data points.
 *
 * Behavior:
 * - When min/max ratio > threshold, the function:
 *   - Reduces the minimum value by minPaddingPercent of the data range (but not below 0 for positive-only data)
 *   - Increases the maximum value by maxPaddingPercent of the data range
 * - For normal data ranges, returns undefined to let ECharts handle auto-scaling
 *
 * @param data - Array of numeric values to calculate range from
 * @param threshold - Ratio threshold (0-1) to determine when to adjust the range (default: 0.8)
 * @param shouldRound - Whether to round the min/max values (default: true)
 * @param decimalPlaces - Number of decimal places to round to when shouldRound is true (default: 0)
 * @returns Object with min and max values for y-axis, or undefined values for auto-scaling
 */
export function calculateYAxisRange(
  data: number[],
  threshold: number = 0.8,
  shouldRound: boolean = true,
  decimalPlaces: number = 0,
): { min: number | undefined; max: number | undefined } {
  // Constants for range adjustment
  const MIN_PADDING_PERCENT = 0.2; // 20% padding below min
  const MAX_PADDING_PERCENT = 0.1; // 10% padding above max

  const validValues = data.filter((val) => val !== undefined && val !== null && !isNaN(val));
  if (validValues.length === 0) return { min: undefined, max: undefined };

  const min = Math.min(...validValues);
  const max = Math.max(...validValues);

  // If the minimum value is large relative to the maximum, adjust the range
  if (min > 0 && min / max > threshold) {
    const range = max - min;
    const adjustedMin = Math.max(0, min - range * MIN_PADDING_PERCENT);
    const adjustedMax = max + range * MAX_PADDING_PERCENT;

    // Round values if needed
    const roundValue = (value: number) => {
      if (!shouldRound) return value;
      const factor = Math.pow(10, decimalPlaces);
      return Math.round(value * factor) / factor;
    };

    return {
      min: roundValue(adjustedMin),
      max: roundValue(adjustedMax),
    };
  }

  return { min: undefined, max: undefined }; // Let ECharts handle auto-scaling
}
